import axiosInstance from './axios.service';
import store from '@/store';

function normalizePointsNamesResponse(response) {
  return Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
}

// ponytail: one shared in-flight request, so concurrent first callers don't each hit /names
let pointsNamesRequest = null;
// Bumped on every new request / clear; a response commits only if no newer request or clear happened.
let pointsNamesVersion = 0;

function clearPointsNamesCache() {
  pointsNamesVersion += 1;
  store.commit('set_pointsNames', null);
  store.commit('set_pointsNamesMerchantId', null);
}

function requestPointsNames() {
  const version = ++pointsNamesVersion;
  const merchantId = store.getters.merchantId;
  const request = axiosInstance
    .get('/operator/points/names')
    .then((response) => {
      const points = normalizePointsNamesResponse(response);
      if (version === pointsNamesVersion) {
        store.commit('set_pointsNames', points);
        store.commit('set_pointsNamesMerchantId', merchantId);
      }
      return points;
    })
    .finally(() => {
      if (pointsNamesRequest === request) pointsNamesRequest = null;
    });
  pointsNamesRequest = request;
  return request;
}

// ponytail: /operator/points/list cached per merchant in module memory; dropped on point create/update/disable.
// Changes made outside this tab show up after a page reload.
let pointsListCache = null;
let pointsListRequest = null;
let pointsListVersion = 0;

function clearPointsListCache() {
  pointsListVersion += 1;
  pointsListCache = null;
  pointsListRequest = null;
}

// Each caller gets its own copy, so a screen mutating its list can't corrupt the shared cache.
const clonePoints = (points) => (points == null ? points : JSON.parse(JSON.stringify(points)));

export async function loadPointsList(data = {}, headers = {}) {
  const url = '/operator/points/list';
  if (Object.keys(data || {}).length || Object.keys(headers || {}).length) {
    return axiosInstance.post(url, data, { headers });
  }

  const merchantId = store.getters.merchantId;
  if (pointsListCache && pointsListCache.merchantId === merchantId) {
    return clonePoints(pointsListCache.points);
  }

  if (!pointsListRequest) {
    const version = pointsListVersion;
    const request = axiosInstance
      .post(url, {}, { headers: {} })
      .then((points) => {
        if (version === pointsListVersion) pointsListCache = { merchantId, points };
        return points;
      })
      .finally(() => {
        if (pointsListRequest === request) pointsListRequest = null;
      });
    pointsListRequest = request;
  }

  return clonePoints(await pointsListRequest);
}

// Refetches after a point mutation; on failure drops the cache so the next getPointsName() retries.
async function reloadPointsNamesCache(action) {
  clearPointsListCache();
  try {
    await requestPointsNames();
  } catch (refreshError) {
    clearPointsNamesCache();
    console.error(`Error refreshing points names after ${action}=>:`, refreshError);
  }
}

function filterDeletedTables(hall) {
  const tables = Array.isArray(hall.tables) ? hall.tables.filter((table) => !table.isDeleted) : [];
  tables.sort((a, b) => {
    const aNum = Number(a?.num);
    const bNum = Number(b?.num);
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;

    const aKey = String(a?.name ?? a?.num ?? a?.numtag ?? '');
    const bKey = String(b?.name ?? b?.num ?? b?.numtag ?? '');
    return aKey.localeCompare(bKey, 'ru', { numeric: true, sensitivity: 'base' });
  });

  return {
    ...hall,
    tables,
  };
}

/* GET POINTS NAME */
export async function getPoints(data = {}, headers = {}) {
  try {
    return await loadPointsList(data, headers);
  } catch (error) {
    console.error(error);
  }
}
export async function getPointsAddress() {
  try {
    const url = `/operator/points/addresses`;
    const response = await axiosInstance.get(url);
    console.log('getPointsAddress:response', response);

    return response;
  } catch (error) {
    console.error(error);
  }
}

// Never rejects: screens call it first in onMounted, a failure must not skip the rest of their loading.
export async function getPointsName() {
  const cached = store.getters.pointsNames;
  if (Array.isArray(cached) && store.getters.pointsNamesMerchantId === store.getters.merchantId) {
    return cached;
  }

  try {
    return await (pointsNamesRequest ?? requestPointsNames());
  } catch (error) {
    console.error('Error loading points names=>:', error);
    return [];
  }
}
export async function getPoint(pointId) {
  const url = `/operator/points/list/${pointId}`;
  try {
    const result = await axiosInstance.get(url);

    return result;
  } catch (error) {
    console.error(error);
  }
}

/* ===========Calls=============== */

/**
 * Retrieves the crew members based on the merchant ID and role.
 * @param {Function} callback - The callback function to be called with the retrieved crew members.
 * @returns {Promise<void>} - A promise that resolves when the crew members are retrieved.
 */
export async function getCrew(pointId, callback) {
  const url = `/operator/personal/employee-list/${pointId}`;
  const response = await axiosInstance.get(url);

  const data = response;
  callback(data);
}

/**
 * Deletes a shift from the database.
 * @param {string} shiftId - The ID of the shift to be deleted.
 * @returns {Promise<void>} - A promise that resolves when the shift is successfully deleted.
 */
export async function deleteShift(shiftId) {
  try {
    const url = `/operator/workaround/shifts/${shiftId}`;
    const response = await axiosInstance.delete(url);
    return response;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Deletes a hall from the database.
 * @param {string} hallId - The ID of the hall to be deleted.
 * @returns {Promise<void>} - A promise that resolves when the hall is successfully deleted.
 */
export async function deleteHall(hallId) {
  try {
    const url = `/operator/workaround/halls/${hallId}`;
    const response = await axiosInstance.delete(url);
    console.log('response:deleteHall', response);

    return response;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Retrieves the last hall number for a given point ID.
 *
 * @param {string} pointId - The ID of the point.
 * @returns {Promise<number>} - The last hall number.
 */
export async function getLastHallNumber(pointId) {
  const url = `/operator/workaround/halls/${pointId}/numtags`;
  try {
    const resp = await axiosInstance.get(url);

    if (!resp.length) {
      return 0;
    }
    const result = +Math.max(...resp) || 0;
    return result;
  } catch (error) {
    console.error(error);
  }
}

export async function getHallNumtags(pointId) {
  const url = `/operator/workaround/halls/${pointId}/numtags`;
  try {
    const result = await axiosInstance.get(url);
    return result || [];
  } catch (error) {
    console.error(error);
    return [];
  }
}
export async function updateShift(shift) {
  const url = `/operator/workaround/shifts/update/${shift.id}`;
  try {
    const response = await axiosInstance.post(url, shift);
    return response;
  } catch (error) {
    console.error(error);
  }
}
export async function createShift(shift) {
  const url = `/operator/workaround/shifts/create`;
  try {
    const response = await axiosInstance.post(url, shift);
    return response;
  } catch (error) {
    console.error(error);
  }
}
export async function updateWorkzone(wz) {
  const url = `/operator/workaround/workzones/update/${wz.id}`;

  try {
    const response = await axiosInstance.post(url, wz);
    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function getWorkzone() {
  const result = {};

  const url = `/operator/workaround/workzones/list
`;
  try {
    const querySnapshot = await axiosInstance.get(url);
    console.log('querySnapshot:result:points-service.js', querySnapshot);
    querySnapshot.forEach((docData) => {
      if (result[docData.pointId]) {
        result[docData.pointId].push(docData);
      } else {
        result[docData.pointId] = [docData];
      }
    });

    return result;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Updates a hall by its point ID.
 * @param {string} pointId - The ID of the point.
 * @param {object} hall - The hall object to update.
 * @returns {Promise<boolean>} - A promise that resolves to true if the update is successful, or rejects with an error if it fails.
 */
export async function updateHall(hall) {
  const url = `/operator/workaround/halls/update/${hall.id}`;
  try {
    const result = await axiosInstance.post(url, hall);
    return result;
  } catch (error) {
    console.error(error);
  }
}
export async function createHall(hall) {
  const url = `/operator/workaround/halls/create
`;
  try {
    const result = await axiosInstance.post(url, hall);
    return result;
  } catch (error) {
    console.error(error);
  }
}
/**
 * Retrieves halls by point ID.
 * @param {string} pointId - The ID of the point.
 * @returns {Promise<Array<Object>>} - A promise that resolves to an array of hall objects.
 */
export async function getHallsByPointId(pointId) {
  const url = `/operator/workaround/halls/points/${pointId}`;
  try {
    const responseSnapshot = await axiosInstance.get(url);
    return responseSnapshot.map(filterDeletedTables);
  } catch (error) {
    console.error(error);
  }
}

/**
 * Retrieves a hall from the database based on the provided hallId.
 * @param {string} hallId - The ID of the hall to retrieve.
 * @returns {Promise<Object>} - A promise that resolves to the hall object.
 */
export async function getHall(hallId) {
  const url = `/operator/workaround/halls/read/${hallId}`;
  try {
    const responseSnapshot = await axiosInstance.get(url);
    return filterDeletedTables(responseSnapshot);
  } catch (error) {
    console.error(error);
  }
}

/**
 * Retrieves a shift based on the shift ID.
 * @param {string} shiftId - The ID of the shift to retrieve.
 * @returns {Promise<Object|null>} - A promise that resolves to the shift object if found, or null if not found.
 */
export async function getShift(shiftId) {
  try {
    const url = `/operator/workaround/shifts/read/${shiftId}`;
    const response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Retrieves all halls associated with the current merchant.
 * @returns {Object} An object containing the halls grouped by pointId.
 */
export async function getAllHalls() {
  const result = {};
  const url = `/operator/workaround/halls/list
`;
  try {
    const resSnapshot = await axiosInstance.get(url);
    resSnapshot.forEach((docData) => {
      if (!docData.disabled) {
        const hall = filterDeletedTables(docData);
        if (result[docData.pointId]) {
          result[docData.pointId].push(hall);
        } else {
          result[docData.pointId] = [hall];
        }
      }
    });
    return result;
  } catch (error) {
    console.error(error);
  }
}
export async function getAllUsers() {
  const result = {};
  const url = `/operator/personal/employee-list
`;
  try {
    const resultSnapshot = await axiosInstance.get(url);
    console.log('result:getAllUsers', result);
    resultSnapshot.forEach((docData) => {
      if (!docData?.disabled) {
        if (Array.isArray(docData.point)) {
          for (const p of docData.point) {
            if (result[p.id]) {
              result[p.id].push(docData);
            } else {
              result[p.id] = [docData];
            }
          }
        } else {
          result[docData.point] = [docData];
        }
      }
    });
    return result;
  } catch (error) {
    console.error(error);
  }
}
/**
 * Retrieves all shifts associated with the current merchant.
 * @returns {Promise<Object>} A promise that resolves to an object containing the shifts grouped by pointId.
 */
export async function getAllShifts() {
  const url = `/operator/workaround/shifts/list
`;
  try {
    const snapshot = await axiosInstance.get(url);
    const resultLocal = {};
    snapshot.map((doc) => {
      const docData = doc;
      if (resultLocal[docData.pointId]) {
        resultLocal[docData.pointId].push(docData);
      } else {
        resultLocal[docData.pointId] = [docData];
      }
    });
    return resultLocal;
  } catch (error) {
    console.error(error);
  }
}

export async function updateQrCode(qrcode) {
  const url = `/operator/qr-code-hash/update/${qrcode.id}`;
  try {
    const result = await axiosInstance.put(url, qrcode);
    return result;
  } catch (error) {
    console.error(error);
  }
}

export async function getPointsData(data = {}, headers = {}) {
  try {
    const result = await loadPointsList(data, headers);
    return result?.filter((point) => !point.disabled);
  } catch (error) {
    console.error(error);
  }
}

/* ===DEPARTMENTS=== */
export async function createDepartment(data, headers = {}) {
  const url = '/operator/departments/create';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    console.log('response:createDepartment', response);
    return response;
  } catch (error) {
    console.error('Error posting data=createDepartment=>:', error);
    return { status: 400, message: error };
  }
} //Create
export async function updateDepartment(data, headers = {}) {
  const url = '/operator/departments/update';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    console.log('response:updateDepartment', response);
    return response;
  } catch (error) {
    console.error('Error posting data=updateDepartment=>:', error);
    return { status: 400, message: error };
  }
} //Update
export async function deleteDepartment(data, headers = {}) {
  const url = '/operator/departments/delete';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    console.log('response:deleteDepartment', response);
    return response;
  } catch (error) {
    console.error('Error posting data=deleteDepartment=>:', error);
    return { status: 400, message: error };
  }
}

export async function getDepartments(data, headers = {}) {
  const url = '/operator/departments/point';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    return response;
  } catch (error) {
    console.error('Error posting data=pointDepartment=>:', error);
    return { status: 400, message: error };
  }
} //point
export async function getMerchantPointsDepartments(data, headers = {}) {
  const url = '/operator/departments/merchant';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    return response;
  } catch (error) {
    console.error('Error posting data=getMerchantPointsDepartments=>:', error);
    return { status: 400, message: error };
  }
} //point
export async function getPointsList(data = {}, headers = {}) {
  try {
    return await loadPointsList(data, headers);
  } catch (error) {
    console.error('Error posting data=getPointsList=>:', error);
    return { status: 400, message: error };
  }
}


export async function getPointById(data, headers = {}) {
  const url = '/operator/point/load';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    return response;
  } catch (error) {
    console.error('Error posting data=getPointById=>:', error);
    return { status: 400, message: error };
  }
} //getPointById

export async function updatePoint(data, headers = {}) {
  const url = '/operator/point/update';
  try {
    console.log('data:updatePoint', data);

    const response = await axiosInstance.post(url, data, { headers });
    await reloadPointsNamesCache('updatePoint');
    return response;
  } catch (error) {
    console.error('Error posting data=updatePoint=>:', error);
    return { status: 400, message: error };
  }
} //updatePoint
export async function createPoint(data, headers = {}) {
  const url = '/operator/point/create';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    await reloadPointsNamesCache('createPoint');
    return response;
  } catch (error) {
    clearPointsNamesCache();
    clearPointsListCache();
    console.error('Error posting data=createPoint=>:', error);
    return { status: 400, message: error };
  }
}
export async function disablePoint(data, headers = {}) {
  const url = '/operator/point/disable';
  try {
    const response = await axiosInstance.post(url, data, { headers });
    await reloadPointsNamesCache('disablePoint');
    return response;
  } catch (error) {
    console.error('Error posting data=disablePoint=>:', error);
    return { status: 400, message: error };
  }
} //disablePoint
